import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "react-loading-overlay-ts";
import {
  listPackageBenefits,
  createPackageBenefit,
  updatePackageBenefit,
  deletePackageBenefit,
  renamePackageBenefitSection,
  swapPackageBenefitOrder,
  swapPackageBenefitSection
} from "../Redux/Action/PackageBenefits";

export default function PackageBenefits() {
  const dispatch = useDispatch();
  const benefits = useSelector((state) => state.PackageBenefits.benefitsArr);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameData, setRenameData] = useState({ category: "", oldSectionTitle: "", newSectionTitle: "" });
  const [editId, setEditId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [newItem, setNewItem] = useState({});
  const [onlyBasic, setOnlyBasic] = useState(false);
  const [onlyMedium, setOnlyMedium] = useState(false);
  const [onlyPremium, setOnlyPremium] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const formatCategory = (c) => {
    if (c === "departures") return "Departures";
    if (c === "inTransit") return "In Transit";
    if (c === "arrivals") return "Arrivals";
    return c;
  };

  const [form, setForm] = useState({
    category: "departures",
    sectionTitle: "",
    name: "",
    basic: false,
    medium: false,
    premium: false,
    notes: ""
  });

  useEffect(() => {
    dispatch(listPackageBenefits()).then(() => setLoading(false));
  }, [dispatch]);

  const openAdd = () => {
    setEditId("");
    setForm({
      category: "departures",
      sectionTitle: "",
      name: "",
      basic: false,
      medium: false,
      premium: false,
      notes: ""
    });
    setShow(true);
  };

  const openEdit = (b) => {
    setEditId(b._id);
    setForm({
      category: b.category,
      sectionTitle: b.sectionTitle,
      name: b.name,
      basic: !!b.basic,
      medium: !!b.medium,
      premium: !!b.premium,
      notes: b.notes || ""
    });
    setShow(true);
  };

  const handleDelete = (id) => {
    setLoading(true);
    dispatch(deletePackageBenefit(id)).then(() => {
      dispatch(listPackageBenefits()).then(() => setLoading(false));
    });
  };

  const handleSubmit = () => {
    setLoading(true);
    if (editId) {
      dispatch(updatePackageBenefit({ id: editId, data: form })).then(() => {
        dispatch(listPackageBenefits()).then(() => setLoading(false));
        setShow(false);
      });
    } else {
      dispatch(createPackageBenefit(form)).then(() => {
        dispatch(listPackageBenefits()).then(() => setLoading(false));
        setShow(false);
      });
    }
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const resetFilters = () => {
    setSelectedCategory("all");
    setSearch("");
    setOnlyBasic(false);
    setOnlyMedium(false);
    setOnlyPremium(false);
    setExpanded({});
  };

  const grouped = useMemo(() => {
    const data = { departures: {}, inTransit: {}, arrivals: {} };
    (benefits || []).forEach((b) => {
      if (onlyBasic && !b.basic) return;
      if (onlyMedium && !b.medium) return;
      if (onlyPremium && !b.premium) return;
      if (search) {
        const s = search.toLowerCase();
        const hit =
          b.name.toLowerCase().includes(s) ||
          (b.sectionTitle || "").toLowerCase().includes(s);
        if (!hit) return;
      }
      if (!data[b.category][b.sectionTitle]) data[b.category][b.sectionTitle] = [];
      data[b.category][b.sectionTitle].push(b);
    });
    return data;
  }, [benefits, search, onlyBasic, onlyMedium, onlyPremium]);

  const categories = ["departures", "inTransit", "arrivals"];

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const expandAll = () => {
    const cats = selectedCategory === "all" ? categories : [selectedCategory];
    setExpanded((prev) => {
      const next = { ...prev };
      cats.forEach((cat) => {
        const sections = grouped[cat] || {};
        Object.keys(sections).forEach((sec) => {
          next[`${cat}:${sec}`] = true;
        });
      });
      return next;
    });
  };
  const collapseAll = () => {
    const cats = selectedCategory === "all" ? categories : [selectedCategory];
    setExpanded((prev) => {
      const next = { ...prev };
      cats.forEach((cat) => {
        const sections = grouped[cat] || {};
        Object.keys(sections).forEach((sec) => {
          next[`${cat}:${sec}`] = false;
        });
      });
      return next;
    });
  };
  const getVisibleSectionKeys = () => {
    const cats = selectedCategory === "all" ? categories : [selectedCategory];
    const keys = [];
    cats.forEach((cat) => {
      const sections = grouped[cat] || {};
      Object.keys(sections).forEach((sec) => keys.push(`${cat}:${sec}`));
    });
    return keys;
  };
  const isAllExpanded = () => {
    const keys = getVisibleSectionKeys();
    if (!keys.length) return false;
    return keys.every((k) => expanded[k]);
  };

  const setNewItemField = (sectionKey, key, value) => {
    setNewItem((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [key]: value }
    }));
  };

  const addUnderSection = (category, sectionTitle) => {
    const sectionKey = `${category}:${sectionTitle}`;
    const payload = {
      category,
      sectionTitle,
      name: (newItem[sectionKey]?.name || "").trim(),
      basic: !!newItem[sectionKey]?.basic,
      medium: !!newItem[sectionKey]?.medium,
      premium: !!newItem[sectionKey]?.premium
    };
    if (!payload.name) {
      alert("Please enter a service name.");
      return;
    }
    setLoading(true);
    dispatch(createPackageBenefit(payload)).then(() => {
      setNewItem((prev) => ({ ...prev, [sectionKey]: { name: "", basic: false, medium: false, premium: false } }));
      dispatch(listPackageBenefits()).then(() => setLoading(false));
    });
  };

  const toggleAvailability = (b, key) => {
    const data = { [key]: !b[key] };
    setLoading(true);
    dispatch(updatePackageBenefit({ id: b._id, data })).then(() => {
      dispatch(listPackageBenefits()).then(() => setLoading(false));
    });
  };

  const openRename = (category, sectionTitle, e) => {
    e.stopPropagation();
    setRenameData({ category, oldSectionTitle: sectionTitle, newSectionTitle: sectionTitle });
    setShowRename(true);
  };

  const handleRenameSubmit = () => {
    if (!renameData.newSectionTitle.trim()) return alert("Please enter a section title");
    if (renameData.newSectionTitle === renameData.oldSectionTitle) return setShowRename(false);
    setLoading(true);
    dispatch(renamePackageBenefitSection(renameData)).then(() => {
      dispatch(listPackageBenefits()).then(() => setLoading(false));
      setShowRename(false);
    });
  };

  const moveBenefit = (id, direction, e) => {
    e.stopPropagation();
    setLoading(true);
    dispatch(swapPackageBenefitOrder({ id, direction })).then(() => {
      dispatch(listPackageBenefits()).then(() => setLoading(false));
    });
  };

  const moveSection = (category, sectionTitle, direction, e) => {
    e.stopPropagation();
    setLoading(true);
    dispatch(swapPackageBenefitSection({ category, sectionTitle, direction })).then(() => {
      dispatch(listPackageBenefits()).then(() => setLoading(false));
    });
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Loading data...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div className="modern-user-section-card" style={{ margin: 12, background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <button
                  className="common-btn"
                  style={{ background: selectedCategory === "all" ? "#034328" : "#e8f9f2", color: selectedCategory === "all" ? "#fff" : "#034328" }}
                  onClick={() => setSelectedCategory("all")}
                >
                  All
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    className="common-btn"
                    style={{ background: selectedCategory === c ? "#034328" : "#e8f9f2", color: selectedCategory === c ? "#fff" : "#034328" }}
                    onClick={() => setSelectedCategory(c)}
                  >
                    {formatCategory(c)}
                  </button>
                ))}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search section or service"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 280, marginLeft: "auto" }}
                />
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#034328" }}>
                  <input type="checkbox" checked={onlyBasic} onChange={(e) => setOnlyBasic(e.target.checked)} />
                  Basic
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#034328" }}>
                  <input type="checkbox" checked={onlyMedium} onChange={(e) => setOnlyMedium(e.target.checked)} />
                  Medium
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#034328" }}>
                  <input type="checkbox" checked={onlyPremium} onChange={(e) => setOnlyPremium(e.target.checked)} />
                  Premium
                </label>
                <i
                  className="fas fa-info-circle"
                  onClick={() => setShowInfo(true)}
                  style={{ color: "#034328", cursor: "pointer", fontSize: 18 }}
                  title="Explain Filters"
                ></i>
                <button className="common-btn" onClick={resetFilters} style={{ marginLeft: "auto" }}>
                  Reset
                </button>
                {isAllExpanded() ? (
                  <button className="common-btn" onClick={collapseAll}>Collapse All</button>
                ) : (
                  <button className="common-btn" onClick={expandAll}>Expand All</button>
                )}
                <button className="common-btn" onClick={() => window.open("/package-preview", "_blank")}>Preview</button>
                <button className="common-btn" onClick={openAdd}>+Add</button>
              </div>

              {(selectedCategory === "all" ? categories : [selectedCategory]).map((cat) => {
                const sections = grouped[cat];
                const sectionTitles = Object.keys(sections).sort((a, b) => {
                  const minOrderA = Math.min(...(sections[a] || []).map((i) => i.order));
                  const minOrderB = Math.min(...(sections[b] || []).map((i) => i.order));
                  return minOrderA - minOrderB;
                });
                if (sectionTitles.length === 0) return null;
                return (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <h4 style={{ fontWeight: 700, color: "#034328", marginBottom: 8 }}>{formatCategory(cat)}</h4>
                    {sectionTitles.map((sec, secIndex) => {
                      const sectionKey = `${cat}:${sec}`;
                      const items = sections[sec] || [];
                      return (
                        <div key={sectionKey} className="modern-user-section-card" style={{ marginBottom: 10, background: "#fff" }}>
                          <div
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                            onClick={() => toggleExpanded(sectionKey)}
                          >
                            <div style={{ fontWeight: 700, color: "#034328", display: "flex", alignItems: "center", gap: 10 }}>
                              {sec}
                              <i
                                className="fas fa-arrow-up"
                                style={{
                                  cursor: secIndex === 0 ? "not-allowed" : "pointer",
                                  opacity: secIndex === 0 ? 0.3 : 1,
                                  fontSize: 12
                                }}
                                onClick={(e) => secIndex !== 0 && moveSection(cat, sec, "up", e)}
                              ></i>
                              <i
                                className="fas fa-arrow-down"
                                style={{
                                  cursor: secIndex === sectionTitles.length - 1 ? "not-allowed" : "pointer",
                                  opacity: secIndex === sectionTitles.length - 1 ? 0.3 : 1,
                                  fontSize: 12
                                }}
                                onClick={(e) => secIndex !== sectionTitles.length - 1 && moveSection(cat, sec, "down", e)}
                              ></i>
                              <i
                                className="fas fa-pencil"
                                style={{ fontSize: 12, color: "#666", cursor: "pointer" }}
                                onClick={(e) => openRename(cat, sec, e)}
                                title="Rename Section"
                              ></i>
                            </div>
                            <div>
                              <i className={`fas ${expanded[sectionKey] ? "fa-chevron-up" : "fa-chevron-down"}`} style={{ color: "#034328" }}></i>
                            </div>
                          </div>
                          {expanded[sectionKey] && (
                            <div style={{ marginTop: 10 }}>
                              <div className="table-responsive">
                                <table className="table table-stripd">
                                  <thead>
                                    <tr>
                                      <th style={{ width: "50%", color: "#034328" }}>Service</th>
                                      <th style={{ width: "10%", color: "#034328" }}>Basic</th>
                                      <th style={{ width: "10%", color: "#034328" }}>Medium</th>
                                      <th style={{ width: "10%", color: "#034328" }}>Premium</th>
                                      <th style={{ width: "20%", color: "#034328" }}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((b, index) => (
                                      <tr key={b._id}>
                                        <td style={{ color: "#111827" }}>{b.name}</td>
                                        <td>
                                          <input type="checkbox" checked={!!b.basic} onChange={() => toggleAvailability(b, "basic")} />
                                        </td>
                                        <td>
                                          <input type="checkbox" checked={!!b.medium} onChange={() => toggleAvailability(b, "medium")} />
                                        </td>
                                        <td>
                                          <input type="checkbox" checked={!!b.premium} onChange={() => toggleAvailability(b, "premium")} />
                                        </td>
                                        <td>
                                          <div className="action-btn" style={{ display: "flex", gap: 10 }}>
                                            <i
                                              className="fas fa-arrow-up"
                                              style={{
                                                cursor: index === 0 ? "not-allowed" : "pointer",
                                                opacity: index === 0 ? 0.3 : 1
                                              }}
                                              onClick={(e) => index !== 0 && moveBenefit(b._id, "up", e)}
                                            ></i>
                                            <i
                                              className="fas fa-arrow-down"
                                              style={{
                                                cursor: index === items.length - 1 ? "not-allowed" : "pointer",
                                                opacity: index === items.length - 1 ? 0.3 : 1
                                              }}
                                              onClick={(e) => index !== items.length - 1 && moveBenefit(b._id, "down", e)}
                                            ></i>
                                            <i className="fas fa-pencil" onClick={() => openEdit(b)}></i>
                                            <i className="fas fa-trash" onClick={() => handleDelete(b._id)}></i>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                    <tr>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Add new service"
                                          value={newItem[sectionKey]?.name || ""}
                                          onChange={(e) => setNewItemField(sectionKey, "name", e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={!!newItem[sectionKey]?.basic}
                                          onChange={(e) => setNewItemField(sectionKey, "basic", e.target.checked)}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={!!newItem[sectionKey]?.medium}
                                          onChange={(e) => setNewItemField(sectionKey, "medium", e.target.checked)}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={!!newItem[sectionKey]?.premium}
                                          onChange={(e) => setNewItemField(sectionKey, "premium", e.target.checked)}
                                        />
                                      </td>
                                      <td>
                                        <button className="common-btn" onClick={() => addUnderSection(cat, sec)}>Add</button>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Modal show={show} onHide={() => setShow(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Update" : "Add"} Package Benefit</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
              >
                <option value="departures">{formatCategory("departures")}</option>
                <option value="inTransit">{formatCategory("inTransit")}</option>
                <option value="arrivals">{formatCategory("arrivals")}</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Section Title</label>
              <input
                type="text"
                className="form-control"
                value={form.sectionTitle}
                onChange={(e) => setField("sectionTitle", e.target.value)}
                placeholder="e.g. Security Check"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Service Name</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Service name"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Availability</label>
              <div className="d-flex" style={{ gap: 16 }}>
                <div>
                  <input
                    type="checkbox"
                    checked={form.basic}
                    onChange={(e) => setField("basic", e.target.checked)}
                  />{" "}
                  Basic
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={form.medium}
                    onChange={(e) => setField("medium", e.target.checked)}
                  />{" "}
                  Medium
                </div>
                <div>
                  <input
                    type="checkbox"
                    checked={form.premium}
                    onChange={(e) => setField("premium", e.target.checked)}
                  />{" "}
                  Premium
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Notes</label>
              <input
                type="text"
                className="form-control"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Optional notes"
              />
            </div>
            <div className="modal-footer">
              <button className="common-btn" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </Modal.Body>
        </Modal>
        <Modal show={showInfo} onHide={() => setShowInfo(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Filter Help</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: 8 }}>
              Basic shows services available in the Basic package.
            </p>
            <p style={{ marginBottom: 8 }}>
              Medium shows services available in the Medium package.
            </p>
            <p style={{ marginBottom: 8 }}>
              Premium shows services available in the Premium package.
            </p>
            <p>
              Selecting multiple filters will show services available in all of the selected packages.
            </p>
          </Modal.Body>
        </Modal>

        <Modal show={showRename} onHide={() => setShowRename(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Rename Section</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Category</label>
              <input type="text" className="form-control" value={formatCategory(renameData.category)} disabled />
            </div>
            <div className="mb-3">
              <label className="form-label">Current Section Title</label>
              <input type="text" className="form-control" value={renameData.oldSectionTitle} disabled />
            </div>
            <div className="mb-3">
              <label className="form-label">New Section Title</label>
              <input
                type="text"
                className="form-control"
                value={renameData.newSectionTitle}
                onChange={(e) => setRenameData((prev) => ({ ...prev, newSectionTitle: e.target.value }))}
                placeholder="Enter new title"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="common-btn" style={{ background: "#ccc", color: "#000" }} onClick={() => setShowRename(false)}>
              Cancel
            </button>
            <button className="common-btn" onClick={handleRenameSubmit}>
              Save Changes
            </button>
          </Modal.Footer>
        </Modal>
      </LoadingOverlay>
    </Layout>
  );
}
